import {
    IIndexChartTypeEnum, IndexTypeEnum, SkipRatesActionTypeEnum, SkipRatesSourceEnum, SkipRatesStageEnum, IIndexCurrencyEnum, IIndexUnitEnum, ScenarioEnum,
    IIndexGranularityEnum,
} from '@/enums/benchmarking/IndexEnums.ts';
import { AncillaryOptionEnum, LeaderboardGasPriceOptionEnum, LeaderboardMarketPriceOptionEnum } from '@/enums/powerMarkets/LeaderboardEnums.ts';
import { IEuropeanIndex, IGbIndex, ISkipRatesIndex } from '@/interfaces/benchmarks/index/IIndex.ts';
import { IIndexOptions } from '@/interfaces/benchmarks/index/IIndexOptions.ts';
import { IIndexPreset } from '@/interfaces/benchmarks/index/IIndexPreset.ts';
import { IDateRangeByString } from '@/interfaces/general/IDateRangeByString.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IDashboardCommonSettings } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import dayjs from 'dayjs';
import { IIdName, IIdNameBoolean } from '@/interfaces/general/IIdName.ts';
import { IIndexType } from '@/interfaces/benchmarks/index/IIndexType.ts';

export const MAX_INDICES_TO_COMPARE = 4;
export const DEFAULT_INDEX_ICON_COLOUR = 'var(--lcpBlue)';
export const DEFAULT_INDEX_ICON_ID = 'calculations';
export const BATTERY_ONLY_WARNING_TEXT = 'This option is for indices containing a battery fuel type';

export const INDEX_TYPES: Array<IIndexType> = [
    { id: IndexTypeEnum.Operational, name: 'Operational Index', icon: ['fas', 'chart-line'] },
    { id: IndexTypeEnum.Optimisation, name: 'Simulated Index', icon: ['fas', 'display-chart-up'] },
    { id: IndexTypeEnum.SkipRates, name: 'Skip Rate', icon: ['fas', 'gauge-simple-high'] },
];

export const DEFAULT_INDEX_DATE_RANGE: IDateRangeByString = {
    from: dayjs().subtract(1, 'month').startOf('month').toISOString(),
    to: dayjs().startOf('month').toISOString(),
};

export const DEFAULT_INDEX_SELECTED_DATE_MODEL: IAdvancedDatePickerModel = {
    dateType: 'fixed',
    dateRange: DEFAULT_INDEX_DATE_RANGE,
    aggregate: {
        granularity: IIndexGranularityEnum.Day,
    },
};

export const DEFAULT_GB_INDEX_FIELDS: IGbIndex = {
    selectedFuel: [],
    selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
    selectedBmStatus: [{ id: 'All', name: 'All' }],
    selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
    selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
    selectedCapacity: [10, 1000],
    selectedDuration: [0, 10],
    selectedPlants: [],
};

export const DEFAULT_SKIP_RATES_INDEX_FIELDS: ISkipRatesIndex = {
    ...DEFAULT_GB_INDEX_FIELDS,
    actionType: SkipRatesActionTypeEnum.Combined,
    source: SkipRatesSourceEnum.Psa,
    stage: SkipRatesStageEnum.Five,
    indexType: IndexTypeEnum.SkipRates,
};

export const DEFAULT_EURO_INDEX_FIELDS: IEuropeanIndex = {
    selectedCapacity: 5,
    selectedDuration: 0.5,
    selectedNumCycles: 1,
    selectedEfficiency: 60,
    selectedRevenueStreams: [],
    selectedScenario: ScenarioEnum.Medium,
    country: 'Germany',
    selectedSocProportion: 50,
};

export const DEFAULT_INDEX_OPTIONS: IIndexOptions = {
    accountForAvailabilityInNormalisation: false,
    marketPrice: LeaderboardMarketPriceOptionEnum.EpexDayAheadPrice,
    gasPrice: LeaderboardGasPriceOptionEnum.DayAheadForward,
    ancillaryRevenueType: AncillaryOptionEnum.FrequencyAndReserve,
    groupDxDirections: false,
    groupDxProducts: false,
    includeCapacityMarket: false,
    includeWholesaleSplit: false,
    showProfit: false,
    bmSplitOutOption: 'NoSplit',
    includeNonDeliveryCharges: false,
    gbSelectedNormalisation: IIndexUnitEnum.Pound,
    euroSelectedNormalisation: IIndexUnitEnum.Pound,
    chargingCostAssumption: 'PreviousEFABlock',
    chargingCostPrice: 'IntradayPrice',
    includeEstimatedChargingCost: false,
    includeFpnFlagOffWholesale: false,
    includeImbalance: false,
    gbCurrency: IIndexCurrencyEnum.Default,
    euroCurrency: IIndexCurrencyEnum.Default,
    chartType: IIndexChartTypeEnum.Revenue,
    reservePenaltySplitOut: 'Ignore',
};

export const DEFAULT_INDEX_OVERRIDES: IDashboardCommonSettings = {
    sharedTooltipAcrossModules: false,
    useYAxisNormalisation: true,
    sharedLegendToggling: true,
};

export const EU_PRESET_ICON_COLOURS: Record<string, string> = {
    Germany: '#c41b1b',
    France: '#0060df',
    Poland: 'var(--lcpWatermelon)',
};

// Permanent identities for the country default groups. Keep these when names or contents change.
export const EURO_INDEX_PRESET_IDS: Record<string, string> = {
    BE: '1458fd49-a8fb-41ca-b711-8723aff5047a',
    FR: 'fd0b03ae-9c23-478c-bda3-1d4eb94256ca',
    DE: '4da10942-5012-4711-89b2-d6896e3e906b',
    NL: '4dddc1df-dcfe-4bf5-9124-00f672695d87',
    PL: 'a4164400-554e-4155-8deb-bfe4d2e4b34b',
    ES: 'eb4e3de1-498c-4b4d-910c-590664e72986',
};

// Preset and individual index IDs are permanent, including when their display names change.
export const PRESET_CCGT_REVENUE: IIndexPreset = {
    id: 'a52a0e65-821a-4ac6-9ac7-0f2e5d161ffb',
    name: 'CCGT Revenue',
    description: 'Presets for CCGT Revenue Indices in Great Britain',
    countryId: 'Gb',
    icon: 'industries-2',
    iconColour: 'var(--lcpBlue)',
    indices: {
        'CCGT Scotland Revenue': {
            id: '0d2755a8-cc1e-42c6-8ec1-52619d2a335a',
            selectedFuel: [{ id: 'CCGT', name: 'CCGT' }],
            selectedZone: [{ id: 'Z1', name: 'Z1' },
                { id: 'Z2', name: 'Z2' },
                { id: 'Z3', name: 'Z3' },
                { id: 'Z4', name: 'Z4' },
                { id: 'Z5', name: 'Z5' },
                { id: 'Z6', name: 'Z6' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [0, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        'CCGT England + Wales Revenue': {
            id: 'd03e626d-2755-4667-8045-e8a3e7cde02f',
            selectedFuel: [{ id: 'CCGT', name: 'CCGT' }],
            selectedZone: [{ id: 'Z7', name: 'Z7' },
                { id: 'Z8', name: 'Z8' },
                { id: 'Z9', name: 'Z9' },
                { id: 'Z10', name: 'Z10' },
                { id: 'Z11', name: 'Z11' },
                { id: 'Z12', name: 'Z12' },
                { id: 'Z13', name: 'Z13' },
                { id: 'Z14', name: 'Z14' },
                { id: 'Z15', name: 'Z15' },
                { id: 'Z16', name: 'Z16' },
                { id: 'Z17', name: 'Z17' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [0, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['CCGT Scotland Revenue', 'CCGT England + Wales Revenue'],
};

export const PRESET_BESS_REVENUE: IIndexPreset = {
    id: '89816f23-343e-4645-a69c-7a28dd56c028',
    name: 'BESS Revenue',
    description: 'Presets for Battery Energy Storage System (BESS) Revenue in Great Britain',
    countryId: 'Gb',
    icon: 'battery-half',
    iconColour: 'var(--lcpSunrise)',
    indices: {
        'Total BESS Revenue': {
            id: '3e40a054-eb74-4bb1-8ad8-cec22bf6243b',
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        '1 Hr BESS Revenue': {
            id: 'a0391112-eeb2-4fcd-ae8e-03ba1796ef2d',
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 1.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        '2 Hr BESS Revenue': {
            id: 'eb8514a0-5927-4318-a7d2-0f07fa3fe146',
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [1.5, 2.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Total BESS Revenue', '1 Hr BESS Revenue', '2 Hr BESS Revenue'],
};

// Permanent identities for restricted CDPQ defaults. Keep these when names or contents change.
export const CDPQ_INDEX_PRESET_IDS = {
    group: 'f4848ebc-caa9-4aa6-9f29-4577a941ed71',
    totalBessRevenue: '3df35137-fc7c-4f40-b849-6d13cc5e497b',
    oneHourBessRevenue: '5fcb6b5c-4201-4c7d-87ab-5f4e5f63cf31',
    twoHourBessRevenue: 'c4c8dc44-f0c2-4cce-bfb3-bc4d938fa4ed',
    bessZoneEightNine: '9c4d1c8d-9b6d-4b84-a4e4-0e0b905d8f5e',
    firstHydroGroup: '83766c9a-a15b-4b56-8d87-0d175afd1188',
    ffestiniog: '2a9d0b5e-5d75-4d36-bc8b-04a01b6dd2c8',
    dinorwig: 'a4e8b8d3-6f43-4fcb-88f2-7c4b7be9de21',
    firstHydroCdpqGroup: 'cc357b18-b56c-443a-afa6-0b2d3c2285ac',
    firstHydroCdpqDinorwig: 'd1d9dbd6-9f7a-4f94-9f9f-f05d6c9e8a1c',
    firstHydroCdpqFfestioniog: 'f6bafac8-3b9e-49d0-99a8-21b7f1b57d62',
    comparableBessGroup: 'f5e84a28-c918-4cb1-aec6-e437c89d270d',
    comparableBessZoneEightNineOneHour: '0a0f1f8d-33be-44bb-a31b-e85fa5b9fc3d',
    comparableBessZoneEightNineTwoHour: '7d8e3c7f-4d0c-4f25-a7d1-5c79eb7b23f1',
    baseCdpqGroup: '162fee65-24b9-4c96-9756-5dab9a5cdcb6',
    baseCdpqZoneEightNine: 'bca098e3-d3f8-42d3-bd44-d155ba4bb996',
    bessRevenueCdpqGroup: '2773428c-91e7-4903-94bc-8a6d6644ad59',
    bessRevenueCdpqTotal: '4c06d6b4-4f7e-48f7-87b1-5d3a91a0c52e',
    bessRevenueCdpqOneHour: 'e4b79d27-48a0-4ba6-97ec-9fd3d587c4d0',
    bessRevenueCdpqTwoHour: 'fdd6e7bc-2c8b-4d4d-89ab-1f4b2e5d1c73',
} as const;

export const CDPQ_INDEX_OPTIONS: IIndexOptions = {
    ...DEFAULT_INDEX_OPTIONS,
    accountForAvailabilityInNormalisation: true,
    marketPrice: LeaderboardMarketPriceOptionEnum.WeightedAverageDayAheadPrice,
    includeNonDeliveryCharges: true,
    gbSelectedNormalisation: IIndexUnitEnum.PoundPerKwPerYear,
    includeImbalance: true,
    reservePenaltySplitOut: 'Absorb',
};

export const CDPQ_INDEX_SELECTED_DATE_MODEL: IAdvancedDatePickerModel = {
    dateType: 'variable',
    dateRange: {
        from: { type: 'dynamic', value: 'StartYear' },
        to: { type: 'dynamic', value: '0' },
    },
    aggregate: {
        granularity: IIndexGranularityEnum.Month,
    },
    rangeSelectType: 'days',
    isHourRangeEnabled: false,
    hoursBefore: null,
    hoursAfter: null,
    daysBefore: null,
    daysAfter: null,
    variableDateStartString: 'StartYear',
    timezone: null,
    periodsPerDay: null,
};

export const CDPQ_FIRST_HYDRO_INDEX_OPTIONS: IIndexOptions = {
    ...CDPQ_INDEX_OPTIONS,
    marketPrice: LeaderboardMarketPriceOptionEnum.EpexDayAheadPrice,
};

export const CDPQ_FIRST_HYDRO_CDPQ_INDEX_OPTIONS: IIndexOptions = {
    ...CDPQ_INDEX_OPTIONS,
    gbCurrency: IIndexCurrencyEnum.GBP,
};

export const CDPQ_FIRST_HYDRO_CDPQ_SELECTED_DATE_MODEL: IAdvancedDatePickerModel = {
    dateType: 'fixed',
    dateRange: {
        from: '2026-08-31T23:00:00Z',
        to: '2026-09-30T23:00:00Z',
    },
    aggregate: {
        granularity: IIndexGranularityEnum.Day,
    },
};

export const PRESET_CDPQ_BESS_REVENUE: IIndexPreset = {
    id: CDPQ_INDEX_PRESET_IDS.group,
    name: 'BESS Revenue',
    description: 'Presets for Battery Energy Storage System (BESS) Revenue in Great Britain',
    countryId: 'Gb',
    icon: 'battery-half',
    iconColour: 'var(--lcpSunrise)',
    options: CDPQ_INDEX_OPTIONS,
    selectedDateModel: CDPQ_INDEX_SELECTED_DATE_MODEL,
    indices: {
        'Total BESS Revenue': {
            id: CDPQ_INDEX_PRESET_IDS.totalBessRevenue,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        '1 Hr BESS Revenue': {
            id: CDPQ_INDEX_PRESET_IDS.oneHourBessRevenue,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 1.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        '2 Hr BESS Revenue': {
            id: CDPQ_INDEX_PRESET_IDS.twoHourBessRevenue,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [1.5, 2.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        'BESS Zone 8 + 9': {
            id: CDPQ_INDEX_PRESET_IDS.bessZoneEightNine,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'Z8', name: 'Z8' }, { id: 'Z9', name: 'Z9' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Total BESS Revenue', '1 Hr BESS Revenue', '2 Hr BESS Revenue', 'BESS Zone 8 + 9'],
};

export const PRESET_CDPQ_FIRST_HYDRO: IIndexPreset = {
    id: CDPQ_INDEX_PRESET_IDS.firstHydroGroup,
    name: 'First Hydro',
    description: '',
    countryId: 'Gb',
    icon: 'calculations',
    iconColour: 'var(--lcpBlue)',
    options: CDPQ_FIRST_HYDRO_INDEX_OPTIONS,
    selectedDateModel: CDPQ_INDEX_SELECTED_DATE_MODEL,
    indices: {
        Ffestiniog: {
            id: CDPQ_INDEX_PRESET_IDS.ffestiniog,
            selectedFuel: [],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0, 10],
            selectedPlants: ['T_FFES-1', 'T_FFES-2', 'T_FFES-3', 'T_FFES-4'],
            indexType: IndexTypeEnum.Operational,
        },
        Dinorwig: {
            id: CDPQ_INDEX_PRESET_IDS.dinorwig,
            selectedFuel: [],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0, 10],
            selectedPlants: ['T_DINO-1', 'T_DINO-2', 'T_DINO-3', 'T_DINO-4', 'T_DINO-5', 'T_DINO-6'],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Ffestiniog', 'Dinorwig'],
};

export const PRESET_CDPQ_FIRST_HYDRO_CDPQ: IIndexPreset = {
    id: CDPQ_INDEX_PRESET_IDS.firstHydroCdpqGroup,
    name: 'First Hydro - CDPQ',
    description: '',
    countryId: 'Gb',
    icon: 'calculations',
    iconColour: 'var(--lcpBlue)',
    options: CDPQ_FIRST_HYDRO_CDPQ_INDEX_OPTIONS,
    selectedDateModel: CDPQ_FIRST_HYDRO_CDPQ_SELECTED_DATE_MODEL,
    indices: {
        Dinorwig: {
            id: CDPQ_INDEX_PRESET_IDS.firstHydroCdpqDinorwig,
            selectedFuel: [],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0, 10],
            selectedPlants: ['T_DINO-1', 'T_DINO-2', 'T_DINO-3', 'T_DINO-4', 'T_DINO-5', 'T_DINO-6'],
            indexType: IndexTypeEnum.Operational,
        },
        Ffestioniog: {
            id: CDPQ_INDEX_PRESET_IDS.firstHydroCdpqFfestioniog,
            selectedFuel: [],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0, 10],
            selectedPlants: ['T_FFES-1', 'T_FFES-2', 'T_FFES-3', 'T_FFES-4'],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Dinorwig', 'Ffestioniog'],
};

export const PRESET_CDPQ_COMPARABLE_BESS: IIndexPreset = {
    id: CDPQ_INDEX_PRESET_IDS.comparableBessGroup,
    name: 'Comparable BESS - CDPQ',
    description: '',
    countryId: 'Gb',
    icon: 'calculations',
    iconColour: 'var(--lcpBlue)',
    options: CDPQ_FIRST_HYDRO_CDPQ_INDEX_OPTIONS,
    selectedDateModel: CDPQ_FIRST_HYDRO_CDPQ_SELECTED_DATE_MODEL,
    indices: {
        'Zone 8 + 9 - 1h': {
            id: CDPQ_INDEX_PRESET_IDS.comparableBessZoneEightNineOneHour,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'Z9', name: 'Z9' }, { id: 'Z8', name: 'Z8' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0.6, 1.4],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        'Zone 8 + 9 - 2h': {
            id: CDPQ_INDEX_PRESET_IDS.comparableBessZoneEightNineTwoHour,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'Z9', name: 'Z9' }, { id: 'Z8', name: 'Z8' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [1.4, 2.4],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Zone 8 + 9 - 1h', 'Zone 8 + 9 - 2h'],
};

export const PRESET_CDPQ_BASE: IIndexPreset = {
    id: CDPQ_INDEX_PRESET_IDS.baseCdpqGroup,
    name: 'Base - CDPQ',
    description: '',
    countryId: 'Gb',
    icon: 'calculations',
    iconColour: 'var(--lcpBlue)',
    options: CDPQ_FIRST_HYDRO_CDPQ_INDEX_OPTIONS,
    selectedDateModel: CDPQ_FIRST_HYDRO_CDPQ_SELECTED_DATE_MODEL,
    indices: {
        'Zone 8 + 9': {
            id: CDPQ_INDEX_PRESET_IDS.baseCdpqZoneEightNine,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'Z9', name: 'Z9' }, { id: 'Z8', name: 'Z8' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [10, 1000],
            selectedDuration: [0.6, 2.4],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Zone 8 + 9'],
};

export const PRESET_CDPQ_BESS_REVENUE_FIXED: IIndexPreset = {
    id: CDPQ_INDEX_PRESET_IDS.bessRevenueCdpqGroup,
    name: 'BESS Revenue - CDPQ',
    description: 'Presets for Battery Energy Storage System (BESS) Revenue in Great Britain',
    countryId: 'Gb',
    icon: 'battery-half',
    iconColour: 'var(--lcpSunrise)',
    options: CDPQ_FIRST_HYDRO_CDPQ_INDEX_OPTIONS,
    selectedDateModel: CDPQ_FIRST_HYDRO_CDPQ_SELECTED_DATE_MODEL,
    indices: {
        'Total BESS Revenue': {
            id: CDPQ_INDEX_PRESET_IDS.bessRevenueCdpqTotal,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        '1 Hr BESS Revenue': {
            id: CDPQ_INDEX_PRESET_IDS.bessRevenueCdpqOneHour,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 1.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
        '2 Hr BESS Revenue': {
            id: CDPQ_INDEX_PRESET_IDS.bessRevenueCdpqTwoHour,
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [1.5, 2.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.Operational,
        },
    },
    indexNameOrder: ['Total BESS Revenue', '1 Hr BESS Revenue', '2 Hr BESS Revenue'],
};

export const PRESET_CCGT_SKIP_RATES: IIndexPreset = {
    id: '10e978ca-e748-4267-8f12-22626698b099',
    name: 'CCGT Skip Rates',
    description: 'Presets for CCGT Skip Rates in Great Britain',
    countryId: 'Gb',
    icon: 'industries-2',
    iconColour: 'var(--lcpBlue)',
    indices: {
        'CCGT Scotland Skip Rates': {
            id: '967bb558-b3f9-4c44-92fa-6681ea8bea87',
            selectedFuel: [{ id: 'CCGT', name: 'CCGT' }],
            selectedZone: [{ id: 'Z1', name: 'Z1' },
                { id: 'Z2', name: 'Z2' },
                { id: 'Z3', name: 'Z3' },
                { id: 'Z4', name: 'Z4' },
                { id: 'Z5', name: 'Z5' },
                { id: 'Z6', name: 'Z6' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [0, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.SkipRates,
            actionType: SkipRatesActionTypeEnum.Combined,
            source: SkipRatesSourceEnum.Psa,
            stage: SkipRatesStageEnum.Five,
        },
        'CCGT England + Wales Skip Rates': {
            id: 'e57cedb2-08db-4512-aa21-bc69d338c83d',
            selectedFuel: [{ id: 'CCGT', name: 'CCGT' }],
            selectedZone: [{ id: 'Z7', name: 'Z7' },
                { id: 'Z8', name: 'Z8' },
                { id: 'Z9', name: 'Z9' },
                { id: 'Z10', name: 'Z10' },
                { id: 'Z11', name: 'Z11' },
                { id: 'Z12', name: 'Z12' },
                { id: 'Z13', name: 'Z13' },
                { id: 'Z14', name: 'Z14' },
                { id: 'Z15', name: 'Z15' },
                { id: 'Z16', name: 'Z16' },
                { id: 'Z17', name: 'Z17' }],
            selectedBmStatus: [{ id: 'All', name: 'All' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [0, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.SkipRates,
            actionType: SkipRatesActionTypeEnum.Combined,
            source: SkipRatesSourceEnum.Psa,
            stage: SkipRatesStageEnum.Five,
        },
    },
    indexNameOrder: ['CCGT Scotland Skip Rates', 'CCGT England + Wales Skip Rates'],
};

export const PRESET_BESS_SKIP_RATES: IIndexPreset = {
    id: '8a59ce57-9e2c-4201-93a7-b3c1aeac6aa9',
    name: 'BESS Skip Rates',
    description: 'Presets for Battery Energy Storage System (BESS) Skip Rates in Great Britain',
    countryId: 'Gb',
    icon: 'battery-half',
    iconColour: 'var(--lcpSunrise)',
    indices: {
        'Total BESS Skip Rates': {
            id: '0c68c5df-4259-466c-a7dd-f831e0374b7b',
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 10],
            selectedPlants: [],
            indexType: IndexTypeEnum.SkipRates,
            actionType: SkipRatesActionTypeEnum.Combined,
            source: SkipRatesSourceEnum.Psa,
            stage: SkipRatesStageEnum.Five,
        },
        '1 Hr BESS Skip Rates': {
            id: '9d502d63-f388-4ce5-995b-4c6ef4e548cb',
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [0, 1.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.SkipRates,
            actionType: SkipRatesActionTypeEnum.Combined,
            source: SkipRatesSourceEnum.Psa,
            stage: SkipRatesStageEnum.Five,
        },
        '2 Hr BESS Skip Rates': {
            id: '6fbcff8c-2804-4af9-96aa-55cb334dd4ee',
            selectedFuel: [{ id: 'Battery', name: 'Battery' }],
            selectedZone: [{ id: 'All Zones', name: 'All Zones' }],
            selectedBmStatus: [{ id: 'Bm', name: 'BM' }],
            selectedOwners: [{ id: 'All Owners', name: 'All Owners' }],
            selectedOptimisers: [{ id: 'All Optimisers', name: 'All Optimisers' }],
            selectedCapacity: [6, 2000],
            selectedDuration: [1.5, 2.5],
            selectedPlants: [],
            indexType: IndexTypeEnum.SkipRates,
            actionType: SkipRatesActionTypeEnum.Combined,
            source: SkipRatesSourceEnum.Psa,
            stage: SkipRatesStageEnum.Five,
        },
    },
    indexNameOrder: ['Total BESS Skip Rates', '1 Hr BESS Skip Rates', '2 Hr BESS Skip Rates'],
};

export const INDEX_GRANULARITY_OPTIONS: Array<IIdName> = [
    { id: 'Day', name: 'Day' },
    { id: 'Week', name: 'Week' },
    { id: 'Month', name: 'Month' },
    { id: 'Year', name: 'Year' },
];

export const SCENARIO_OPTIONS: Array<IIdName> = [
    { id: ScenarioEnum.Low, name: 'Downside' },
    { id: ScenarioEnum.Medium, name: 'Central' },
    { id: ScenarioEnum.High, name: 'Upside' },
];

export const BM_STATUS_OPTIONS: Array<IIdName> = [
    { id: 'All', name: 'All' },
    { id: 'Bm', name: 'BM' },
    { id: 'BmNoFpn', name: 'BM (No FPN)' },
    { id: 'NonBm', name: 'Non-BM' },
];

export const GB_UNIT_OPTIONS: Array<IIdName> = [
    { id: IIndexUnitEnum.Pound, name: '£' },
    { id: IIndexUnitEnum.PoundPerMw, name: '£/MW' },
    { id: IIndexUnitEnum.PoundPerMwh, name: '£/MWh', info: 'Limited to BESS only' },
    { id: IIndexUnitEnum.PoundPerKwPerYear, name: '£/kW/year' },
    { id: IIndexUnitEnum.PoundPerThroughput, name: '£/MWh Exported', info: 'Limited to BM assets only' },
];

export const EURO_UNIT_OPTIONS: Array<IIdName> = [
    { id: IIndexUnitEnum.Pound, name: '€' },
    { id: IIndexUnitEnum.PoundPerMw, name: '€/MW' },
    { id: IIndexUnitEnum.PoundPerMwh, name: '€/MWh', info: 'Limited to BESS only' },
    { id: IIndexUnitEnum.PoundPerKwPerYear, name: '€/kW/year' },
];

export const CURRENCY_OPTIONS: Array<IIdName> = [
    { id: IIndexCurrencyEnum.Default, name: 'Default' },
    { id: IIndexCurrencyEnum.GBP, name: 'GBP' },
    { id: IIndexCurrencyEnum.EUR, name: 'EUR' },
];

export const PROFIT_REVENUE_OPTIONS: Array<IIdNameBoolean> = [
    { id: true, name: 'Show Profit' },
    { id: false, name: 'Show Revenue' },
];

export const RESERVE_PENALTY_SPLIT_OUT_OPTIONS: Array<IIdName> = [{
    id: 'Ignore', name: 'Ignore Reserve Penalties', info: 'Ancillary revenue columns show availability payments only.',
}, {
    id: 'Show', name: 'Show Reserve Penalties', info: 'Add extra columns to show reserve penalties, not affecting revenue columns.',
}, {
    id: 'Absorb', name: 'Absorb Reserve Penalties', info: 'Ancillary revenue columns include reserve penalties.',
}];

export const MARKET_PRICE_OPTIONS: Array<IIdName> = [
    { id: 'WeightedAverageDayAheadPrice', name: 'Weighted Average DA', info: 'Volume weighted average day-ahead price of EPEX and N2EX' },
    { id: 'EpexDayAheadPrice', name: 'DA (EPEX)', info: 'Day-ahead price - EPEX hourly auction' },
    { id: 'NordpoolDayAheadPrice', name: 'DA (N2EX)', info: 'Day-ahead price - N2EX hourly auction' },
    { id: 'IntradayPrice', name: 'MID Price', info: 'Intraday (MID / APX) price - EPEX continuous auction' },
    { id: 'BestPrice', name: 'Best Price', info: 'Optimal price out of EPEX DA, N2EX DA and Intraday' },
];

export const GAS_PRICE_OPTIONS: Array<IIdName> = [
    { id: 'DayAheadForward', name: 'Day Ahead Forward' },
    { id: 'DayAheadSpot', name: 'Day Ahead Spot' },
    { id: 'WithinDaySpot', name: 'Within Day Spot', info: 'One-day delayed (not available in realtime)' },
    { id: 'CheapestPrice', name: 'Cheapest Price' },
];

export const BM_SPLIT_OPTIONS: Array<IIdName> = [{
    id: 'NoSplit', name: 'No Split Out',
}, {
    id: 'SystemEnergySplit', name: 'System / Energy',
}, {
    id: 'BidOfferSplit', name: 'Bid / Offer',
}];

export const ANCILLARY_REVENUE_OPTIONS: Array<IIdName> = [{
    id: 'FrequencyAndReserve', name: 'Frequency and Reserve', info: 'Show aggregated ancillary revenues by Frequency and Reserve',
}, {
    id: 'ByProduct', name: 'By Product', info: 'Show ancillary revenues by separate products',
}, {
    id: 'ByDirection', name: 'By Direction', info: 'Show ancillary revenues by product and direction',
}];

export const CHARGING_PRICE_OPTIONS: Array<IIdName> = [{
    id: 'WeightedAverageDayAheadPrice', name: 'Weighted Average DA', info: 'Volume weighted average day-ahead price of EPEX and N2EX',
}, {
    id: 'EpexDayAheadPrice', name: 'DA (EPEX)', info: 'Day-ahead price - EPEX hourly auction',
}, {
    id: 'NordpoolDayAheadPrice', name: 'DA (N2EX)', info: 'Day-ahead price - N2EX hourly auction',
}, {
    id: 'IntradayPrice', name: 'MID Price', info: 'Intraday (MID / APX) price - EPEX continuous auction',
}, {
    id: 'SystemPrice', name: 'System Price',
}];

export const CHARGING_ASSUMPTION_OPTIONS: Array<IIdName> = [{
    id: 'OptimalPricePrev12Hours', name: 'Optimal price from previous 12 hrs', info: 'For a given EFA block of ancillary volume, aggregate the volumes for each service separately, <br> take the best price over the 12 hours before the start of the EFA block and multiply.',
}, {
    id: 'PreviousEFABlock', name: 'Average price from previous EFA block', info: 'For a given EFA block of ancillary volume, aggregate the volumes for each service separately, <br> take the average price from the EFA block previous to the EFA block of delivery and multiply.',
}, {
    id: 'CurrentSp', name: 'Price of current SP', info: 'Multiply each period of ancillary volume and service by the chosen price at that settlement period',
}];

export const CHART_TYPE_OPTIONS: Array<IIdName> = [
    { id: IIndexChartTypeEnum.Revenue, name: 'Revenue' },
    { id: IIndexChartTypeEnum.Cycles, name: 'Cycles', info: 'Only available for European assets' },
    { id: IIndexChartTypeEnum.EnergyBuy, name: 'Energy Bought', info: 'Only available for European assets' },
    { id: IIndexChartTypeEnum.EnergySell, name: 'Energy Sold', info: 'Only available for European assets' },
    { id: IIndexChartTypeEnum.MeanParticipation, name: 'Average Ancillary Participation', info: 'Only available for European assets' },
];
